'use client';

import {
  AlphabeticalGridFilter,
  AlphabeticalMobileFilter,
  ColumnInfoGroup,
  Container,
  PageContent
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { CSSProperties, useId, useRef, useState } from 'react';
import { defaultLanguage } from '@stereoagency/constants';
import flush from 'just-flush';
import type { diseasesQuery } from './diseasesQuery';
import { findInKeyArray } from '@/utils';
import { DiseaseModal } from '@/components/modals/DiseaseModal';

type Props = {
  diseases: (typeof diseasesQuery)[1];
};

export default function DiseaseListingSection({ diseases }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const id = useId();
  const { t, lang } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const [diseaseOpen, setDiseaseOpen] = useState<
    (typeof diseases)[number] | null
  >(null);

  const handleMobileSelect = (letter: string) => {
    const group = containerRef.current?.querySelector(
      `[id="disease-listing-section-${id}-${letter}"]`
    );
    if (group) {
      const dpr = window.devicePixelRatio ?? 1;
      const rect = group.getBoundingClientRect();
      const top =
        rect.top +
        (document.documentElement.scrollTop || document.body.scrollTop) -
        dpr * 120;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  };

  const sortedDiseases = diseases.sort((a, b) => {
    const aName = a.name
      ? findInKeyArray(a.name, [lang, defaultLanguage.id])
      : null;
    const bName = b.name
      ? findInKeyArray(b.name, [lang, defaultLanguage.id])
      : null;
    if (typeof aName?.value === 'string' && !(typeof bName?.value)) return -1;
    if (!(typeof aName?.value === 'string') && typeof bName?.value) return 1;
    return aName?.value?.localeCompare(bName?.value ?? '') ?? 0;
  });

  const grouped = sortedDiseases.reduce<Map<string, typeof diseases>>(
    (map, disease) => {
      const name = disease.name
        ? findInKeyArray(disease.name, [lang, defaultLanguage.id])
        : null;
      const firstLetter = name?.value ? name.value[0].toUpperCase() : null;
      if (name && firstLetter) {
        const group =
          map.get(firstLetter) ??
          (map.set(firstLetter, []) && map.get(firstLetter)!);
        group.push(disease);
      }
      return map;
    },
    new Map()
  );
  const allLetterOptions = Array.from(grouped.keys()).sort();
  const visibleOptions = selected ? [selected] : allLetterOptions;

  return (
    <Container ref={containerRef} name={DiseaseListingSection.name}>
      <div className="grid gap-6 grid-cols-[1fr_auto] grid-flow-row auto-rows-max md:grid-cols-[auto_1fr] md:gap-10 lg:gap-12">
        <div className="order-last md:order-first">
          <AlphabeticalGridFilter
            resetAction={{
              label: t('global:all'),
              suffix: `(${diseases.length})`
            }}
            include={allLetterOptions}
            value={selected}
            onChange={setSelected}
            className="sticky top-6 left-0 hidden md:flex"
          />
          <AlphabeticalMobileFilter
            include={allLetterOptions}
            onSelect={handleMobileSelect}
            className="sticky top-6 left-0 flex md:hidden"
          />
        </div>
        <PageContent className="my-0 md:my-0 md:col-start-2">
          <ColumnInfoGroup
            entries={visibleOptions.map((letter) => {
              const group = grouped.get(letter) ?? [];
              return {
                title: letter,
                entries: flush(
                  group.map((entry, entryIndex) => {
                    const name = entry.name
                      ? findInKeyArray(entry.name, [lang, defaultLanguage.id])
                      : null;
                    if (!name?.value) {
                      return null;
                    }
                    const group3split = group.length / 3;
                    const group2split = group.length / 2;
                    const mdColIndex = group3split
                      ? Math.floor(entryIndex / group3split) + 1
                      : 1;
                    const smColIndex = group2split
                      ? Math.floor(entryIndex / group2split) + 1
                      : 1;

                    return {
                      id: `disease-listing-section-${id}-${letter}`,
                      title: name.value,
                      action: {
                        href: '/',
                        onClick(event) {
                          event.preventDefault();
                          setDiseaseOpen(entry);
                        }
                      },
                      style: {
                        '--col-start-md': mdColIndex,
                        '--col-start-sm': smColIndex
                      } as CSSProperties,
                      className: `sm:col-start-[var(--col-start-sm)] md:col-start-[var(--col-start-md)]`
                    };
                  })
                )
              };
            })}
          />
        </PageContent>
      </div>

      <DiseaseModal
        disease={diseaseOpen}
        onClose={() => setDiseaseOpen(null)}
      />
    </Container>
  );
}
